import React from "react";

const RedemptionResultModal = ({ isOpen, onClose, result, memberName, offerTitle, offerDiscountType, error }) => {
    if (!isOpen) return null;

    const isSuccess = result === "success";

    const formatOfferName = (name) => {
        if (!name) return "";
        return name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const offerDisplayName = offerDiscountType ? formatOfferName(offerDiscountType) : offerTitle;

    return (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4">
            {/* Modal Wrapper - Ensures close button isn't clipped */}
            <div className="relative w-full max-w-[480px] animate-[slideIn_0.3s_cubic-bezier(0.16,1,0.3,1)]">

                {/* Close Button - Outside the scrollable area */}
                <button
                    onClick={onClose}
                    className="absolute -top-5 -right-5 w-[42px] h-[42px] bg-[#00ADEF] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-[2001]"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                </button>

                {/* Main Content Area */}
                <div
                    className="bg-white rounded-[12px] p-6 sm:p-10 w-full shadow-[0px_20px_60px_rgba(0,0,0,0.15)] max-h-[90vh] overflow-y-auto"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    <style>{`
                        div::-webkit-scrollbar {
                            display: none;
                        }
                    `}</style>

                    <div className="flex flex-col items-center">
                        {/* Status Icon */}
                        <div className={`w-[90px] h-[90px] rounded-full flex items-center justify-center mb-6 ${isSuccess ? 'bg-[#E7F9F0]' : 'bg-[#FFF2F2]'}`}>
                            <div className={`w-[55px] h-[55px] rounded-full flex items-center justify-center ${isSuccess ? 'bg-[#20C997]' : 'bg-[#FF4D4F]'}`}>
                                {isSuccess ? (
                                    <svg width="28" height="20" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18.3333 1.66663L6.66667 13.3333L1.66667 8.33329" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1 1L17 17M1 17L17 1" stroke="white" strokeWidth="3" strokeLinecap="round" />
                                    </svg>
                                )}
                            </div>
                        </div>

                        {/* Title & Subtitle */}
                        <h2 className="text-[28px] font-[600] text-[#303030] mb-1 font-poppins text-center">
                            {isSuccess ? "Code is Valid" : "Code is Invalid"}
                        </h2>
                        <p className="text-[#8C8C8C] text-[16px] font-[500] mb-8 font-poppins text-center">
                            {isSuccess ? "Verification successful" : "Verification unsuccessful"}
                        </p>

                        {/* Member Card */}
                        <div className="w-full bg-[#F9FAFB] rounded-[20px] border border-[#F2F4F7] p-6 mb-6">
                            <div className="flex items-center gap-4">
                                <div className="w-[48px] h-[48px] rounded-full bg-[#EBF8FF] flex items-center justify-center shrink-0">
                                    <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M15 15.75V14.25C15 13.4544 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4544 3 14.25V15.75" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z" stroke="#00ADEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <div className="flex flex-col items-start">
                                    <p className="text-[10px] font-[700] text-[#A3A3A3] uppercase tracking-[1.5px] mb-0.5">Redeeming Member</p>
                                    <p className="text-[16px] font-[600] text-[#303030] font-poppins">
                                        {(!memberName || memberName === "Unknown") ? "Unclaimed" : memberName}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Redeemable Offer Section */}
                        <div className="w-full mb-8">
                            <p className="text-[10px] font-[700] text-[#A3A3A3] uppercase tracking-[1.5px] mb-4 text-center">Redeemable Offer</p>
                            {isSuccess ? (
                                <div className="flex items-center justify-center gap-2 py-4 px-6 bg-[#FAFDFF] border border-[#D0EFFF] rounded-[10px] w-fit mx-auto max-w-full">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7708 21.0143C12.5279 21.115 12.2675 21.115 12.0045 21.115C11.7415 21.115 11.4811 21.115 11.2382 21.0143C10.9953 20.9135 10.7747 20.766 10.589 20.58L2 12V2H12L20.59 10.59C20.9631 10.9648 21.1725 11.4722 21.1725 12.001C21.1725 12.5298 20.9631 13.0372 20.59 13.41V13.41Z" stroke="#00ADEF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M7 7H7.01" stroke="#00ADEF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p className="text-[14px] font-[600] text-[#00ADEF] font-poppins text-center leading-tight">
                                        {offerDisplayName}
                                    </p>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center gap-2 py-4 px-6 bg-[#FFFBFB] border-[1.5px] border-dashed border-[#FFB2B2] rounded-[10px] w-fit mx-auto max-w-full">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" stroke="#FF4D4F" strokeWidth="2" />
                                        <path d="M12 8V12" stroke="#FF4D4F" strokeWidth="2.5" strokeLinecap="round" />
                                        <path d="M12 16H12.01" stroke="#FF4D4F" strokeWidth="3" strokeLinecap="round" />
                                    </svg>
                                    <p className="text-[13px] font-[500] text-[#A63535] font-poppins text-center leading-tight">
                                        {error === "Not Found" ? "No active offer found for this code." : (error || "No active offer found for this code.")}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        <button
                            onClick={onClose}
                            className="w-full h-[60px] bg-[#00ADEF] hover:bg-[#0099D4] text-white rounded-[14px] text-[18px] font-[600] font-poppins transition-all shadow-md active:scale-[0.98]"
                        >
                            {isSuccess ? "Done" : "Try Again"}
                        </button>
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes slideIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
};

export default RedemptionResultModal;
